import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import {
  ArrowLeft,
  Ban,
  CalendarX,
  KeyRound,
  Mail,
  ShieldCheck,
  Ticket,
  Trash2,
  UserCog,
  UserCheck,
} from "lucide-react";
import {
  adminApi,
  MAX_CREDITS_PER_GRANT,
  MAX_REASON_LENGTH,
  TAG_TYPE_LABEL,
  TAG_TYPES,
  type PhysicalTagType,
  type AdminUserDetail,
  type AuditLogRow,
  type GrantTagCreditsInput,
  type StripeMode,
} from "@/lib/api";
import { LIST_STALE_MS, queryKeys } from "@/lib/queries";
import { itemStatusTone } from "@/lib/status-tone";
import { formatLondon, isoUtc } from "@/lib/format-time";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Field, Select } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { StatusPill, type Tone } from "@/components/StatusPill";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authed/users_/$uid")({
  component: UserDetailPage,
});

function UserDetailPage() {
  const { uid } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [actionMessage, setActionMessage] = useState<{
    tone: "success" | "danger";
    text: string;
  } | null>(null);
  const [impersonationToken, setImpersonationToken] = useState<{
    token: string;
    expiresAt: number;
  } | null>(null);

  const detail = useQuery({
    queryKey: queryKeys.users.detail(uid),
    queryFn: async () => (await adminApi.getUser()({ uid })).data,
  });

  function flash(tone: "success" | "danger", text: string) {
    setActionMessage({ tone, text });
    window.setTimeout(() => setActionMessage(null), 5000);
  }

  function refresh() {
    void qc.invalidateQueries({ queryKey: queryKeys.users.detail(uid) });
    void qc.invalidateQueries({ queryKey: queryKeys.users.all });
  }

  const suspend = useMutation({
    mutationFn: async (reason?: string) =>
      (await adminApi.suspendUser()({ uid, reason })).data,
    onSuccess: () => {
      flash("success", "User suspended.");
      refresh();
    },
    onError: (e: Error) => flash("danger", e.message),
  });

  const restore = useMutation({
    mutationFn: async () => (await adminApi.restoreUser()({ uid })).data,
    onSuccess: () => {
      flash("success", "User restored.");
      refresh();
    },
    onError: (e: Error) => flash("danger", e.message),
  });

  const resetDob = useMutation({
    mutationFn: async () => (await adminApi.resetDateOfBirth()({ uid })).data,
    onSuccess: () => {
      flash("success", "Date of birth cleared.");
      refresh();
    },
    onError: (e: Error) => flash("danger", e.message),
  });

  const setRole = useMutation({
    mutationFn: async (role: "platform_admin" | "end_user") =>
      (await adminApi.setRole()({ uid, role })).data,
    onSuccess: (_, role) => {
      flash("success", `Role set to ${role}.`);
      refresh();
    },
    onError: (e: Error) => flash("danger", e.message),
  });

  const sendReset = useMutation({
    mutationFn: async () => (await adminApi.sendPasswordReset()({ uid })).data,
    onSuccess: () => flash("success", "Reset link emailed to user."),
    onError: (e: Error) => flash("danger", e.message),
  });

  const impersonate = useMutation({
    mutationFn: async () => (await adminApi.impersonateUser()({ uid })).data,
    onSuccess: ({ customToken, expiresAt }) => {
      setImpersonationToken({ token: customToken, expiresAt });
      flash("success", "Impersonation token minted (5 min).");
    },
    onError: (e: Error) => flash("danger", e.message),
  });

  const requestDeletion = useMutation({
    mutationFn: async (reason?: string) =>
      (await adminApi.requestAccountDeletion()({ uid, reason })).data,
    onSuccess: ({ scheduledDeletionAt }) => {
      flash(
        "success",
        `Deletion scheduled for ${format(new Date(scheduledDeletionAt), "yyyy-MM-dd HH:mm")} UTC.`,
      );
      refresh();
    },
    onError: (e: Error) => flash("danger", e.message),
  });

  const grantCredits = useMutation({
    mutationFn: async (input: Omit<GrantTagCreditsInput, "uid">) =>
      (await adminApi.grantTagCredits()({ uid, ...input })).data,
    onSuccess: ({ applied, type, balance }) => {
      const noun = TAG_TYPE_LABEL[type];
      flash(
        "success",
        applied === 0
          ? `Nothing to remove — ${noun} balance is already 0.`
          : `${applied > 0 ? "Added" : "Removed"} ${Math.abs(applied)} ${noun} credit(s); balance now ${balance}.`,
      );
      refresh();
    },
    onError: (e: Error) => flash("danger", e.message),
  });

  const setStripeMode = useMutation({
    mutationFn: async (mode: StripeMode) =>
      (await adminApi.setStripeMode()({ uid, mode })).data,
    onSuccess: ({ mode }) => {
      flash("success", STRIPE_MODE_COPY[mode].success);
      // stripeMode isn't shown in the users list — refetch the detail only.
      void qc.invalidateQueries({ queryKey: queryKeys.users.detail(uid) });
    },
    onError: (e: Error) => flash("danger", e.message),
  });

  const clearRemovalClock = useMutation({
    mutationFn: async () => (await adminApi.clearRemovalClock()({ uid })).data,
    onSuccess: ({ cleared }) => {
      flash("success", cleared ? "Removal clock cleared." : "No removal clock was running.");
      void qc.invalidateQueries({ queryKey: queryKeys.users.detail(uid) });
    },
    onError: (e: Error) => flash("danger", e.message),
  });

  function confirmThen(message: string, fn: () => void) {
    if (window.confirm(message)) fn();
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/users" })}>
          <ArrowLeft className="mr-1 h-4 w-4" /> Users
        </Button>
        <span className="text-muted-foreground">/</span>
        <span className="font-mono text-xs text-muted-foreground">{uid}</span>
      </div>

      {detail.isLoading ? (
        <Card>
          <CardContent className="p-8 text-sm text-muted-foreground">Loading…</CardContent>
        </Card>
      ) : detail.isError ? (
        <Card>
          <CardContent className="p-8 text-sm text-destructive">
            Failed to load user: {(detail.error as Error).message}
          </CardContent>
        </Card>
      ) : detail.data ? (
        <>
          <UserHeader user={detail.data} />

          {actionMessage ? (
            <div
              className={cn(
                "rounded-md border p-3 text-sm",
                actionMessage.tone === "success"
                  ? "border-success/40 bg-success/5 text-success"
                  : "border-destructive/40 bg-destructive/5 text-destructive",
              )}
            >
              {actionMessage.text}
            </div>
          ) : null}

          {impersonationToken ? (
            <ImpersonationTokenCard
              token={impersonationToken.token}
              expiresAt={impersonationToken.expiresAt}
              onClose={() => setImpersonationToken(null)}
            />
          ) : null}

          <Tabs defaultValue="profile">
            <TabsList>
              <TabsTrigger value="profile">Profile</TabsTrigger>
              <TabsTrigger value="items">Items</TabsTrigger>
              <TabsTrigger value="subscription">Subscription</TabsTrigger>
              <TabsTrigger value="credits">Tag credits</TabsTrigger>
              <TabsTrigger value="login">Login history</TabsTrigger>
              <TabsTrigger value="audit">Audit</TabsTrigger>
            </TabsList>

            <TabsContent value="profile">
              <ProfileTab
                user={detail.data}
                onSuspend={() =>
                  confirmThen(
                    "Suspend this user? They won't be able to sign in.",
                    () => suspend.mutate(undefined),
                  )
                }
                onRestore={() => restore.mutate()}
                onMakeAdmin={() =>
                  confirmThen(
                    "Promote this user to platform_admin? They'll get full admin access.",
                    () => setRole.mutate("platform_admin"),
                  )
                }
                onRevokeAdmin={() =>
                  confirmThen(
                    "Revoke admin role from this user?",
                    () => setRole.mutate("end_user"),
                  )
                }
                onSendReset={() => sendReset.mutate()}
                onResetDob={() =>
                  confirmThen(
                    "Clear this user's date of birth? They will be asked for it again at their next purchase.",
                    () => resetDob.mutate(),
                  )
                }
                onImpersonate={() =>
                  confirmThen(
                    "Generate a 5-minute impersonation token? This action is logged.",
                    () => impersonate.mutate(),
                  )
                }
                onRequestDeletion={() =>
                  confirmThen(
                    "Schedule this account for deletion in 7 days? The user will receive an email with a cancel link.",
                    () => requestDeletion.mutate(undefined),
                  )
                }
                pending={
                  suspend.isPending ||
                  restore.isPending ||
                  setRole.isPending ||
                  sendReset.isPending ||
                  resetDob.isPending ||
                  impersonate.isPending ||
                  requestDeletion.isPending
                }
              />
            </TabsContent>

            <TabsContent value="items">
              <ItemsTab uid={uid} />
            </TabsContent>

            <TabsContent value="subscription">
              <SubscriptionTab
                user={detail.data}
                onSetStripeMode={(mode) =>
                  confirmThen(STRIPE_MODE_COPY[mode].confirm, () =>
                    setStripeMode.mutate(mode),
                  )
                }
                pending={setStripeMode.isPending}
              />
            </TabsContent>

            <TabsContent value="credits">
              <TagCreditsTab
                user={detail.data}
                onGrant={(input) => grantCredits.mutate(input)}
                pending={grantCredits.isPending}
                onClearRemovalClock={() =>
                  confirmThen(
                    "Clear the removal clock? The account's paused digital tags will no longer be removed on the scheduled date. This is audit-logged.",
                    () => clearRemovalClock.mutate(),
                  )
                }
                clockPending={clearRemovalClock.isPending}
              />
            </TabsContent>

            <TabsContent value="login">
              <LoginHistoryTab user={detail.data} />
            </TabsContent>

            <TabsContent value="audit">
              <AuditTab uid={uid} />
            </TabsContent>
          </Tabs>
        </>
      ) : null}
    </div>
  );
}

function UserHeader({ user }: { user: AdminUserDetail }) {
  return (
    <Card>
      <CardContent className="flex flex-wrap items-start justify-between gap-4 p-5">
        <div className="space-y-1">
          <div className="text-xs font-mono text-muted-foreground">{user.uid}</div>
          <h1 className="text-xl font-semibold tracking-tight">
            {user.displayName || user.email || "(no name)"}
          </h1>
          <div className="flex flex-wrap items-center gap-2">
            {user.email ? (
              <span className="text-sm text-muted-foreground">{user.email}</span>
            ) : null}
            {user.emailVerified ? (
              <StatusPill tone="success">verified</StatusPill>
            ) : (
              <StatusPill tone="warning">unverified</StatusPill>
            )}
            {user.disabled ? (
              <StatusPill tone="danger">disabled</StatusPill>
            ) : (
              <StatusPill tone="success">active</StatusPill>
            )}
            {user.customClaims?.role === "platform_admin" ? (
              <StatusPill tone="info">admin</StatusPill>
            ) : null}
            {user.scheduledDeletionAt ? (
              <StatusPill tone="danger">
                deletion scheduled {format(new Date(user.scheduledDeletionAt), "yyyy-MM-dd")}
              </StatusPill>
            ) : null}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-right text-xs">
          <span className="text-muted-foreground">Items</span>
          <span className="font-mono">{user.itemsCreated.toLocaleString()}</span>
          <span className="text-muted-foreground">Pending claims</span>
          <span className="font-mono">{user.pendingClaims.toLocaleString()}</span>
          <span className="text-muted-foreground">Joined</span>
          <span className="font-mono">
            {user.creationTime ? format(new Date(user.creationTime), "yyyy-MM-dd") : "—"}
          </span>
          <span className="text-muted-foreground">Last sign-in</span>
          <span className="font-mono">
            {user.lastSignInTime ? format(new Date(user.lastSignInTime), "yyyy-MM-dd") : "—"}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function ProfileTab({
  user,
  onSuspend,
  onRestore,
  onMakeAdmin,
  onRevokeAdmin,
  onSendReset,
  onResetDob,
  onImpersonate,
  onRequestDeletion,
  pending,
}: {
  user: AdminUserDetail;
  onSuspend: () => void;
  onRestore: () => void;
  onMakeAdmin: () => void;
  onRevokeAdmin: () => void;
  onSendReset: () => void;
  onResetDob: () => void;
  onImpersonate: () => void;
  onRequestDeletion: () => void;
  pending: boolean;
}) {
  const isAdmin = user.customClaims?.role === "platform_admin";
  const doc = user.doc ?? {};

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardContent className="space-y-2 p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Identity
          </h2>
          <Row label="UID">{user.uid}</Row>
          <Row label="Email">{user.email ?? "—"}</Row>
          <Row label="Phone">{user.phoneNumber ?? "—"}</Row>
          <Row label="Display name">{user.displayName ?? "—"}</Row>
          <Row label="Providers">{user.providerIds.join(", ") || "—"}</Row>
          <Row label="School">
            {valueOrDash(doc.schoolName)}
            {doc.schoolUrn ? ` (${doc.schoolUrn as string})` : ""}
          </Row>
          <Row label="Last refresh">
            {user.lastRefreshTime
              ? format(new Date(user.lastRefreshTime), "yyyy-MM-dd HH:mm:ss")
              : "—"}
          </Row>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Actions
          </h2>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {user.disabled ? (
              <Button variant="outline" disabled={pending} onClick={onRestore}>
                <UserCheck className="mr-2 h-4 w-4" /> Restore
              </Button>
            ) : (
              <Button variant="destructive" disabled={pending} onClick={onSuspend}>
                <Ban className="mr-2 h-4 w-4" /> Suspend
              </Button>
            )}
            {isAdmin ? (
              <Button variant="outline" disabled={pending} onClick={onRevokeAdmin}>
                <UserCog className="mr-2 h-4 w-4" /> Revoke admin
              </Button>
            ) : (
              <Button variant="outline" disabled={pending} onClick={onMakeAdmin}>
                <ShieldCheck className="mr-2 h-4 w-4" /> Make admin
              </Button>
            )}
            <Button variant="outline" disabled={pending || !user.email} onClick={onSendReset}>
              <Mail className="mr-2 h-4 w-4" /> Email password reset
            </Button>
            {doc.dateOfBirth ? (
              <Button variant="outline" disabled={pending} onClick={onResetDob}>
                <CalendarX className="mr-2 h-4 w-4" /> Reset date of birth
              </Button>
            ) : null}
            <Button
              variant="outline"
              disabled={pending || user.disabled}
              onClick={onImpersonate}
            >
              <KeyRound className="mr-2 h-4 w-4" /> Impersonate (5 min)
            </Button>
            <Button
              variant="destructive"
              disabled={pending || Boolean(user.scheduledDeletionAt)}
              onClick={onRequestDeletion}
              className="sm:col-span-2"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {user.scheduledDeletionAt ? "Deletion scheduled" : "Schedule deletion (7d)"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Suspend, restore, role change, and deletion are audit-logged.
          </p>
        </CardContent>
      </Card>

      <Card className="lg:col-span-2">
        <CardContent className="space-y-2 p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Firestore document
          </h2>
          {Object.keys(doc).length === 0 ? (
            <p className="text-xs text-muted-foreground">No users/{user.uid} document.</p>
          ) : (
            <pre className="overflow-x-auto rounded-md bg-muted p-3 text-xs">
              {JSON.stringify(doc, null, 2)}
            </pre>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// No client route consumes an impersonation token today — the Flutter app
// (web or otherwise) has no `signInWithCustomToken` / token-login route to
// deep-link into (checked lib/ — only the `impersonatedBy` claim banner
// exists, for a session an admin is already inside). Until that lands, the
// only place this 5-minute token can go is the clipboard; the improvement
// here is to stop it outliving its TTL — we wipe the clipboard automatically
// once the token expires and surface the countdown prominently instead of a
// static "~5 min" hint.

// Clears the clipboard only if it still holds the exact token we put there —
// an operator may have copied something else in the meantime, and reading
// the clipboard can throw (permission denied / unsupported), in which case
// we skip the wipe rather than risk destroying unknown clipboard content.
async function clearClipboardIfMatches(expected: string) {
  try {
    const current = await navigator.clipboard.readText();
    if (current === expected) {
      await navigator.clipboard.writeText("");
    }
  } catch {
    // Denied or unsupported — leave the clipboard alone.
  }
}

function ImpersonationTokenCard({
  token,
  expiresAt,
  onClose,
}: {
  token: string;
  expiresAt: number;
  onClose: () => void;
}) {
  const [nowSeconds, setNowSeconds] = useState(() => Math.floor(Date.now() / 1000));
  const [copied, setCopied] = useState(false);
  const clearTimerRef = useRef<number | null>(null);
  const tokenRef = useRef(token);
  tokenRef.current = token;

  useEffect(() => {
    const interval = window.setInterval(() => setNowSeconds(Math.floor(Date.now() / 1000)), 1000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    return () => {
      // The card unmounts on Dismiss (or navigation), which would otherwise
      // silently cancel the pending wipe while the token stays on the
      // clipboard — contradicting the card's own copy. If a wipe was still
      // pending, run it now instead.
      if (clearTimerRef.current != null) {
        window.clearTimeout(clearTimerRef.current);
        void clearClipboardIfMatches(tokenRef.current);
      }
    };
  }, []);

  const expiresIn = Math.max(0, expiresAt - nowSeconds);
  const expired = expiresIn <= 0;

  function handleCopy() {
    void navigator.clipboard.writeText(token);
    setCopied(true);
    if (clearTimerRef.current != null) window.clearTimeout(clearTimerRef.current);
    // Best-effort: wipe the clipboard once the token's own TTL elapses so it
    // can't be pasted somewhere after it's already dead.
    clearTimerRef.current = window.setTimeout(() => {
      clearTimerRef.current = null;
      void clearClipboardIfMatches(token);
    }, expiresIn * 1000);
  }

  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Impersonation token</h3>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Dismiss
          </Button>
        </div>
        <p
          className={cn(
            "text-xs font-medium",
            expired ? "text-destructive" : expiresIn <= 60 ? "text-warning" : "text-muted-foreground",
          )}
        >
          {expired
            ? "Expired — no longer valid. Generate a new one."
            : `Expires in ${Math.floor(expiresIn / 60)}m ${String(expiresIn % 60).padStart(2, "0")}s (${format(new Date(expiresAt * 1000), "HH:mm:ss")}).`}
        </p>
        <p className="text-xs text-muted-foreground">
          Sign into a separate Firebase app instance with this custom token to act as the user.
          Audit-logged. Do not reuse. The clipboard is cleared automatically once the token
          expires.
        </p>
        <div className="flex gap-2">
          <input
            readOnly
            value={token}
            disabled={expired}
            className="flex h-9 w-full rounded-md border border-input bg-muted px-3 font-mono text-xs disabled:opacity-50"
            onFocus={(e) => e.currentTarget.select()}
          />
          <Button variant="outline" size="sm" onClick={handleCopy} disabled={expired}>
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ItemsTab({ uid }: { uid: string }) {
  const items = useQuery({
    queryKey: queryKeys.items.list({ userId: uid }),
    queryFn: async () => {
      const res = await adminApi.listItems()({ userId: uid, limit: 50 });
      return res.data.items;
    },
    staleTime: LIST_STALE_MS,
  });

  return (
    <Card>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs uppercase text-muted-foreground">
              <th className="px-4 py-2 font-medium">Name</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium">Category</th>
              <th className="px-4 py-2 font-medium">Created</th>
              <th className="px-4 py-2 font-medium">ID</th>
            </tr>
          </thead>
          <tbody>
            {items.isLoading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            ) : items.isError ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center">
                  <span className="text-destructive">{(items.error as Error).message}</span>
                </td>
              </tr>
            ) : !items.data || items.data.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  No items.
                </td>
              </tr>
            ) : (
              items.data.map((it) => (
                <tr key={it.id} className="border-b">
                  <td className="px-4 py-2 font-medium">{it.name}</td>
                  <td className="px-4 py-2">
                    <StatusPill tone={itemStatusTone(it.status)}>
                      {it.status}
                    </StatusPill>
                  </td>
                  <td className="px-4 py-2 text-xs">{it.category}</td>
                  <td className="px-4 py-2 font-mono text-xs">
                    {it.createdAt ? format(new Date(it.createdAt), "yyyy-MM-dd") : "—"}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{it.id}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

/** Per-mode copy for the Stripe-mode row: its pill, and the confirm/toast for switching TO that mode. */
const STRIPE_MODE_COPY: Record<
  StripeMode,
  { label: string; tone: Tone; other: StripeMode; confirm: string; success: string }
> = {
  live: {
    label: "live",
    tone: "neutral",
    other: "test",
    confirm: "Route this account's purchases back to live Stripe (real money)?",
    success: "Account back on live Stripe.",
  },
  test: {
    label: "sandbox",
    tone: "warning",
    other: "live",
    confirm: "Route this account's purchases to the Stripe sandbox (test cards, no real money)?",
    success: "Account now pays on the Stripe sandbox.",
  },
};

function SubscriptionTab({
  user,
  onSetStripeMode,
  pending,
}: {
  user: AdminUserDetail;
  onSetStripeMode: (mode: StripeMode) => void;
  pending: boolean;
}) {
  const doc = user.doc ?? {};
  const stripeMode: StripeMode = doc.stripeMode === "test" ? "test" : "live";
  const current = STRIPE_MODE_COPY[stripeMode];
  const tier = (doc.subscriptionTier as string | undefined) ?? "free";
  const platform = doc.subscriptionPlatform as string | null | undefined;
  const endDate = doc.subscriptionEndDate as number | undefined;
  return (
    <Card>
      <CardContent className="space-y-2 p-5">
        <Row label="Tier">
          <StatusPill tone={tier === "free" ? "neutral" : "info"}>
            {platform ? `${tier} · ${platform}` : tier}
          </StatusPill>
        </Row>
        <Row label="End date">
          {endDate ? format(new Date(endDate), "yyyy-MM-dd HH:mm") : "—"}
        </Row>
        <Row label="Stripe mode">
          <div className="flex items-center gap-2">
            <StatusPill tone={current.tone}>{current.label}</StatusPill>
            <Button
              size="sm"
              variant="outline"
              disabled={pending}
              onClick={() => onSetStripeMode(current.other)}
            >
              Switch to {STRIPE_MODE_COPY[current.other].label}
            </Button>
          </div>
        </Row>
        <p className="pt-2 text-xs text-muted-foreground">
          Refunds and Stripe-side actions live on the Tag orders page (Phase 5).
        </p>
      </CardContent>
    </Card>
  );
}

/**
 * Balances per tag type plus the manual grant/removal form behind
 * adminGrantTagCredits — tester packs, replacements, goodwill. Negative
 * credits remove unspent ones (the server clamps at 0).
 */
function TagCreditsTab({
  user,
  onGrant,
  pending,
  onClearRemovalClock,
  clockPending,
}: {
  user: AdminUserDetail;
  onGrant: (input: Omit<GrantTagCreditsInput, "uid">) => void;
  pending: boolean;
  onClearRemovalClock: () => void;
  clockPending: boolean;
}) {
  const [type, setType] = useState<PhysicalTagType>("qr_sticker");
  const [credits, setCredits] = useState("1");
  const [reason, setReason] = useState("");
  const amount = Number(credits);
  const valid =
    Number.isInteger(amount) &&
    amount !== 0 &&
    Math.abs(amount) <= MAX_CREDITS_PER_GRANT &&
    reason.trim().length > 0;

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardContent className="space-y-2 p-5">
          {TAG_TYPES.map((t) => (
            <Row key={t} label={`${TAG_TYPE_LABEL[t]} credits`}>
              {user.tagCredits[t]}
            </Row>
          ))}
          <p className="pt-2 text-xs text-muted-foreground">
            Unspent credits available to claim tags. Paid orders grant them via Stripe;
            grants and removals here are audit-logged.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-2 p-5">
          <Row label="Free signup tag">
            {user.signupTagId ? (
              <span className="font-mono text-xs">{user.signupTagId}</span>
            ) : (
              "not granted (unverified)"
            )}
          </Row>
          <Row label="Generated tags">
            {user.generatedTags} / {user.generatedTagLimit}
          </Row>
          <Row label="Removal clock">
            {user.digitalTagRemovalDueAt ? (
              <span className="flex flex-wrap items-center gap-2">
                <span title={isoUtc(user.digitalTagRemovalDueAt)}>{formatLondon(user.digitalTagRemovalDueAt)}</span>
                <Button size="sm" variant="outline" disabled={clockPending} onClick={onClearRemovalClock}>
                  Clear removal clock
                </Button>
              </span>
            ) : (
              "none running"
            )}
          </Row>
          <Row label="Courtesy return">
            {user.pausedCourtesyReturnId ? (
              <span className="font-mono text-xs">{user.pausedCourtesyReturnId}</span>
            ) : (
              "—"
            )}
          </Row>
          <p className="pt-2 text-xs text-muted-foreground">
            Digital tags the account generated. The signup tag is granted on email verification
            and never pauses; subscriber slots are consumed for life (re-assigning reuses them)
            and pause while the subscription has lapsed. See the Tags page (origin = generated).
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 p-5">
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Type">
              <Select value={type} onChange={(e) => setType(e.target.value as PhysicalTagType)}>
                {TAG_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {TAG_TYPE_LABEL[t]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Credits (− removes)">
              <Input
                value={credits}
                onChange={(e) => setCredits(e.target.value)}
                type="number"
                min={-MAX_CREDITS_PER_GRANT}
                max={MAX_CREDITS_PER_GRANT}
                className="w-32"
              />
            </Field>
          </div>
          <Field label="Reason (required)">
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={MAX_REASON_LENGTH}
              placeholder="e.g. tester pack, replacement for damaged sticker"
            />
          </Field>
          <Button
            disabled={pending || !valid}
            onClick={() => onGrant({ type, credits: amount, reason: reason.trim() })}
          >
            <Ticket className="mr-2 h-4 w-4" />
            {pending ? "Applying…" : amount < 0 ? "Remove credits" : "Grant credits"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function LoginHistoryTab({ user }: { user: AdminUserDetail }) {
  return (
    <Card>
      <CardContent className="space-y-2 p-5">
        <Row label="Created">
          {user.creationTime ? format(new Date(user.creationTime), "yyyy-MM-dd HH:mm:ss") : "—"}
        </Row>
        <Row label="Last sign-in">
          {user.lastSignInTime
            ? format(new Date(user.lastSignInTime), "yyyy-MM-dd HH:mm:ss")
            : "—"}
        </Row>
        <Row label="Last token refresh">
          {user.lastRefreshTime
            ? format(new Date(user.lastRefreshTime), "yyyy-MM-dd HH:mm:ss")
            : "—"}
        </Row>
        <Row label="Email verified">{user.emailVerified ? "yes" : "no"}</Row>
        <p className="pt-2 text-xs text-muted-foreground">
          Per-event sign-in history requires logging auth events into the audit log — deferred.
          Until then, see the Audit tab for any portal actions taken on this user.
        </p>
      </CardContent>
    </Card>
  );
}

function AuditTab({ uid }: { uid: string }) {
  const q = useInfiniteQuery({
    queryKey: ["users", "audit", uid],
    queryFn: async ({ pageParam }) => {
      const res = await adminApi.queryAuditLogs()({
        userId: uid,
        limit: 25,
        cursor: pageParam ?? null,
      });
      return res.data;
    },
    initialPageParam: null as number | null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: LIST_STALE_MS,
  });

  const rows: AuditLogRow[] = q.data?.pages.flatMap((p) => p.logs) ?? [];

  return (
    <Card>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs uppercase text-muted-foreground">
              <th className="px-4 py-2 font-medium">Time</th>
              <th className="px-4 py-2 font-medium">Action</th>
              <th className="px-4 py-2 font-medium">Actor</th>
              <th className="px-4 py-2 font-medium">Result</th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">
                  No audit entries for this user.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id} className="border-b">
                  <td className="whitespace-nowrap px-4 py-2 font-mono text-xs">
                    {format(new Date(r.timestamp), "yyyy-MM-dd HH:mm:ss")}
                  </td>
                  <td className="px-4 py-2 text-xs">{r.action ?? r.eventType}</td>
                  <td className="px-4 py-2 text-xs">{r.actorType}</td>
                  <td className="px-4 py-2">
                    <StatusPill tone={r.result === "success" ? "success" : "danger"}>
                      {r.result}
                    </StatusPill>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {q.hasNextPage ? (
          <div className="flex justify-end border-t p-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void q.fetchNextPage()}
              disabled={q.isFetchingNextPage}
            >
              {q.isFetchingNextPage ? "Loading…" : "Load more"}
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 py-1">
      <span className="text-xs font-medium uppercase text-muted-foreground">{label}</span>
      <span className="text-right font-mono text-xs">{children}</span>
    </div>
  );
}

function valueOrDash(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  return String(v);
}

